/**
 * Platform Subscription Handler
 *
 * Strict TypeScript conversion for the "Become a PIFster" button flow.
 */

import type { FluentCartModalContext } from './fluentcart/fluentcart-modal-context';
import { fetchDemoSession } from './demo/demo-api';

interface PlatformBootstrapWindow extends Window {
  readonly PIFSTER_BOOTSTRAP?: {
    readonly hasModernSubscription?: boolean;
    readonly fluentCartProfileUrl?: string;
  } | null;
}

type ExistingSubscriptionDetail = {
  readonly manageUrl: string | null;
};

type PifsterFluentcartContext = {
  readonly charityId: number | null;
  readonly charityName: string;
  readonly periodId: number | null;
  readonly intentToken: string | null;
  readonly beneficiary: null;
  readonly donateHrefFallback: string;
  readonly bonusRatio?: number;
  readonly bonusCap?: number;
  readonly mode: 'vote_plus_donate' | 'donate_only';
  readonly entry: 'return_status_cta' | 'header_join';
};

type PifsterFluentcartEventDetail = Partial<FluentCartModalContext>;

interface VoteIntentContext {
  readonly intentToken: string | null;
  readonly charityId: number | null;
  readonly periodId: number | null;
}

function getPlatformCheckoutUrl(): string {
  const urlElement = document.getElementById('pifster-platform-checkout-url');
  const url = urlElement?.getAttribute('data-checkout-url') ?? '';
  return url.trim() !== '' ? url : '#';
}

function getVoteIntentContext(): VoteIntentContext {
  const params = new URLSearchParams(window.location.search);
  const intentToken = params.get('intent_token');
  const charityIdRaw = params.get('charity_id');
  const periodIdRaw = params.get('period_id');

  return {
    intentToken: intentToken && intentToken.trim() !== '' ? intentToken : null,
    charityId: charityIdRaw && charityIdRaw.trim() !== '' ? Number.parseInt(charityIdRaw, 10) || null : null,
    periodId: periodIdRaw && periodIdRaw.trim() !== '' ? Number.parseInt(periodIdRaw, 10) || null : null,
  };
}

function isFluentCartModalReady(): boolean {
  return document.documentElement.dataset['pifsterFluentcartReady'] === '1'
    && document.getElementById('pifster-fluentcart-modal') instanceof HTMLElement;
}

function handlePlatformSubscribeClick(event: MouseEvent): void {
  event.preventDefault();

  const voteContext = getVoteIntentContext();
  const mode: PifsterFluentcartContext['mode'] = voteContext.intentToken ? 'vote_plus_donate' : 'donate_only';
  const entry: PifsterFluentcartContext['entry'] = voteContext.intentToken ? 'return_status_cta' : 'header_join';
  const checkoutUrl = getPlatformCheckoutUrl();

  const detail: PifsterFluentcartEventDetail = {
    charityId: voteContext.charityId,
    charityName: '',
    periodId: voteContext.periodId,
    intentToken: voteContext.intentToken,
    beneficiary: null,
    donateHrefFallback: checkoutUrl,
    mode,
    entry,
  };

  document.dispatchEvent(new CustomEvent<PifsterFluentcartEventDetail>('pifster:open-fluentcart-modal', {
    detail,
  }));
}

function hasModernSubscription(): boolean {
  const bootstrapWindow = window as PlatformBootstrapWindow;
  return bootstrapWindow.PIFSTER_BOOTSTRAP?.hasModernSubscription === true;
}

function resolveManageUrl(): string | null {
  const bootstrapWindow = window as PlatformBootstrapWindow;
  const manageUrl = bootstrapWindow.PIFSTER_BOOTSTRAP?.fluentCartProfileUrl ?? '';
  return typeof manageUrl === 'string' && manageUrl.trim() !== '' ? manageUrl : null;
}

function notifyExistingSubscription(manageUrl: string | null): boolean {
  const event = new CustomEvent<ExistingSubscriptionDetail>('pifster:existing-subscription-detected', {
    detail: { manageUrl },
    cancelable: true,
  });
  document.dispatchEvent(event);
  return event.defaultPrevented;
}

function handleExistingSubscription(): boolean {
  if (!hasModernSubscription()) {
    return false;
  }

  const manageUrl = resolveManageUrl();
  const prevented = notifyExistingSubscription(manageUrl);
  if (prevented) {
    return true;
  }

  if (manageUrl) {
    const url = new URL(manageUrl, window.location.origin);
    url.searchParams.set('notice', 'already_subscribed');
    window.location.href = url.toString();
  }

  return true;
}

function attachHandlers(): void {
  document.addEventListener('click', (event: MouseEvent): void => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const button = target.closest<HTMLElement>('[data-pifster-become]');
    if (!button) {
      return;
    }

    if (!isFluentCartModalReady()) {
      return;
    }

    event.preventDefault();
    void fetchDemoSession().then((demoSession) => {
      if (demoSession === null && handleExistingSubscription()) {
        return;
      }

      handlePlatformSubscribeClick(event);
    });
  });
}

function init(): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachHandlers);
    return;
  }

  attachHandlers();
}

init();

export {};
